import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, FileText, CheckCircle2, Zap } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SERVICES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: SERVICES[0].title,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service_interest: SERVICES[0].title, message: "" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="border-b-2 border-black">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-80px)]">
        {/* Left: contact info */}
        <div className="bg-zinc-950 text-white p-8 md:p-16 lg:p-20 flex flex-col justify-between">
          <div>
            <p className="overline text-zinc-500"><span className="text-[#E62117] mr-2">◆</span>Get in touch</p>
            <h1 data-testid="contact-title" className="font-display text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.9] mt-6">
              Let's get<br />
              <span className="text-[#E62117]">powered up.</span>
            </h1>
            <p className="mt-8 text-lg text-zinc-400 leading-relaxed max-w-md">
              Call, email or send us a message. We respond fast — most enquiries get a same-day reply.
            </p>

            <div className="mt-12 space-y-8">
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="contact-info-phone"
                className="group flex items-start gap-5 border-t border-zinc-800 pt-6 hover:border-[#E62117] transition-colors"
              >
                <div className="w-12 h-12 border-2 border-white group-hover:border-[#E62117] group-hover:text-[#E62117] flex items-center justify-center transition-colors">
                  <Phone size={20} />
                </div>
                <div>
                  <p className="overline text-zinc-500">Phone</p>
                  <p className="font-display text-3xl md:text-4xl mt-1">{CONTACT.phone}</p>
                </div>
              </a>

              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-info-email"
                className="group flex items-start gap-5 border-t border-zinc-800 pt-6 hover:border-[#E62117] transition-colors"
              >
                <div className="w-12 h-12 border-2 border-white group-hover:border-[#E62117] group-hover:text-[#E62117] flex items-center justify-center transition-colors">
                  <Mail size={20} />
                </div>
                <div>
                  <p className="overline text-zinc-500">Email</p>
                  <p className="font-display text-2xl md:text-3xl mt-1 break-all">{CONTACT.email}</p>
                </div>
              </a>

              <div data-testid="contact-info-address" className="flex items-start gap-5 border-t border-zinc-800 pt-6">
                <div className="w-12 h-12 border-2 border-white flex items-center justify-center">
                  <MapPin size={20} />
                </div>
                <div>
                  <p className="overline text-zinc-500">Postal</p>
                  <p className="font-display text-2xl md:text-3xl mt-1">{CONTACT.address}</p>
                </div>
              </div>

              <div data-testid="contact-info-abn" className="flex items-start gap-5 border-t border-zinc-800 pt-6">
                <div className="w-12 h-12 border-2 border-white flex items-center justify-center">
                  <FileText size={20} />
                </div>
                <div>
                  <p className="overline text-zinc-500">ABN</p>
                  <p className="font-mono-brand text-xl md:text-2xl mt-1 tracking-wider">{CONTACT.abn}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 flex items-center gap-3 text-[#E62117] font-mono-brand uppercase text-xs tracking-widest">
            <Zap size={14} />
            <span>Melbourne · Regional Victoria</span>
          </div>
        </div>

        {/* Right: form */}
        <div className="bg-white p-8 md:p-16 lg:p-20 flex flex-col justify-center">
          {submitted ? (
            <div data-testid="contact-success" className="max-w-lg">
              <div className="w-16 h-16 border-2 border-black bg-[#E62117] text-white flex items-center justify-center">
                <CheckCircle2 size={32} />
              </div>
              <h2 className="font-display text-5xl md:text-6xl uppercase mt-8 leading-[0.95]">
                Message<br />received.
              </h2>
              <p className="mt-6 text-lg text-zinc-700">
                Thanks — we'll come back to you shortly. If it's urgent, call {CONTACT.phone} directly.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => setSubmitted(false)}
                className="btn-secondary mt-8"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={onSubmit} className="max-w-xl space-y-6">
              <p className="overline">Enquiry form</p>
              <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] mt-2">
                Tell us what you need.
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Your Name *</label>
                  <input
                    data-testid="contact-input-name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="input-brutal"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Phone *</label>
                  <input
                    data-testid="contact-input-phone"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    className="input-brutal"
                    placeholder="04XX XXX XXX"
                  />
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Email *</label>
                <input
                  data-testid="contact-input-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="input-brutal"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="overline block mb-2">Service of interest *</label>
                <select
                  data-testid="contact-select-service"
                  required
                  value={form.service_interest}
                  onChange={update("service_interest")}
                  className="input-brutal appearance-none bg-white"
                  style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='%23000' d='M6 8L2 4h8z'/></svg>\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center" }}
                >
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Other / Not sure">Other / Not sure</option>
                </select>
              </div>

              <div>
                <label className="overline block mb-2">Message *</label>
                <textarea
                  data-testid="contact-input-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="input-brutal resize-none"
                  placeholder="Tell us about your project, address (optional), timeline…"
                />
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="btn-primary w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Sending…" : "Send Message"}
              </button>

              <p className="text-xs text-zinc-500 font-mono-brand uppercase tracking-widest">
                We reply within 1 business day.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
